// 유효성 검사 유틸리티 함수

/**
 * 이메일 형식 검증
 * @param email 검증할 이메일
 * @returns 유효한 이메일인지 여부
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * 전화번호 형식 검증 (한국 휴대폰 번호)
 * @param phoneNumber 검증할 전화번호
 * @returns 유효한 전화번호인지 여부
 */
export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  const phoneRegex = /^01[0-9]-?[0-9]{4}-?[0-9]{4}$/;
  return phoneRegex.test(phoneNumber.replace(/-/g, ''));
};

/**
 * 사업자등록번호 형식 검증
 * @param businessNumber 검증할 사업자등록번호
 * @returns 유효한 사업자등록번호인지 여부
 */
export const isValidBusinessNumber = (businessNumber: string): boolean => {
  const regex = /^\d{3}-?\d{2}-?\d{5}$/;
  return regex.test(businessNumber);
};

/**
 * 날짜 문자열 형식 검증 (YYYY-MM-DD)
 * @param dateString 검증할 날짜 문자열
 * @returns 유효한 날짜 형식인지 여부
 */
export const isValidDateFormat = (dateString: string): boolean => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) return false;
  
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * 필수 필드 검증
 * @param value 검증할 값
 * @returns 값이 존재하는지 여부
 */
export const isRequired = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

/**
 * 최소 길이 검증
 * @param value 검증할 문자열
 * @param minLength 최소 길이
 * @returns 최소 길이를 만족하는지 여부
 */
export const hasMinLength = (value: string, minLength: number): boolean => {
  return value.length >= minLength;
};

/**
 * 최대 길이 검증
 * @param value 검증할 문자열
 * @param maxLength 최대 길이
 * @returns 최대 길이를 만족하는지 여부
 */
export const hasMaxLength = (value: string, maxLength: number): boolean => {
  return value.length <= maxLength;
};

/**
 * 숫자 범위 검증
 * @param value 검증할 숫자
 * @param min 최소값
 * @param max 최대값
 * @returns 범위 내에 있는지 여부
 */
export const isInRange = (value: number, min: number, max: number): boolean => {
  return value >= min && value <= max;
};

/**
 * 양수 검증
 * @param value 검증할 숫자
 * @returns 양수인지 여부
 */
export const isPositiveNumber = (value: number): boolean => {
  return value > 0;
};

/**
 * 음이 아닌 수 검증 (0 또는 양수)
 * @param value 검증할 숫자
 * @returns 음이 아닌 수인지 여부
 */
export const isNonNegativeNumber = (value: number): boolean => {
  return value >= 0;
};

/**
 * 유효한 팀 이름 검증
 * @param teamName 검증할 팀 이름
 * @returns 유효한 팀 이름인지 여부
 */
export const isValidTeamName = (teamName: string): boolean => {
  if (!teamName || teamName.trim().length === 0) return false;
  if (teamName.length > 50) return false;
  
  // 특수문자 제한 (한글, 영문, 숫자, 공백, 일부 특수문자만 허용)
  const validPattern = /^[가-힣a-zA-Z0-9\s\-_()]+$/;
  return validPattern.test(teamName);
};

/**
 * 유효한 직원 이름 검증
 * @param name 검증할 이름
 * @returns 유효한 이름인지 여부
 */
export const isValidEmployeeName = (name: string): boolean => {
  if (!name || name.trim().length === 0) return false;
  if (name.length < 2 || name.length > 30) return false;
  
  // 한글 또는 영문 이름만 허용
  const koreanPattern = /^[가-힣\s]+$/;
  const englishPattern = /^[a-zA-Z\s]+$/;
  
  return koreanPattern.test(name) || englishPattern.test(name);
};

/**
 * 폼 데이터 유효성 검사 결과 타입
 */
export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * 폼 데이터 일괄 유효성 검사
 * @param data 검사할 데이터
 * @param rules 검사 규칙
 * @returns 유효성 검사 결과
 */
export const validateForm = <T extends Record<string, any>>(
  data: T,
  rules: Record<keyof T, ((value: any) => string | null)[]>
): ValidationResult => {
  const errors: Record<string, string> = {};
  
  Object.entries(rules).forEach(([field, validators]) => {
    const value = data[field as keyof T];
    
    for (const validator of validators) {
      const error = validator(value);
      if (error) {
        errors[field] = error;
        break;
      }
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * 전화번호에서 숫자만 추출 (저장용)
 * @param phoneNumber 포맷된 전화번호 또는 숫자 문자열
 * @returns 숫자만 포함된 문자열
 */
export const extractPhoneNumbers = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/[^\d]/g, '');
};

/**
 * 전화번호를 한국 형식으로 포맷팅 (표시용)
 * @param phoneNumber 숫자만 포함된 전화번호
 * @returns 하이픈이 포함된 포맷된 전화번호
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  
  // 숫자만 추출
  const numbers = phoneNumber.replace(/[^\d]/g, '');
  
  if (numbers.length === 0) return '';
  
  // 한국 전화번호 패턴에 따라 포맷팅
  if (numbers.length === 11) {
    // 010-1234-5678, 011-123-4567 등
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
  } else if (numbers.length === 10) {
    // 02-1234-5678, 031-123-4567 등 (지역번호)
    if (numbers.startsWith('02')) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    }
  } else if (numbers.length === 9) {
    // 02-123-4567 등
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5)}`;
  } else if (numbers.length === 8) {
    // 1588-1234 등
    return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
  }
  
  // 기타 경우는 그대로 반환
  return numbers;
};